import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Component, Inject, OnInit, Optional, ViewChild } from '@angular/core';
import { FormControl, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import {
  APPLICATION_STATUS,
  FIRM_APPLICATION_TYPES,
  FIRM_SERVICE_FOR,
  LEAD_BASED_PAINT,
} from '@shared/utils/app-static-data';
import { IAccountAffiliation } from 'app/models/common/account-affiliation';
import { IFirm } from 'app/models/firms/firm';
import { IFirmAddress } from 'app/models/firms/firm-address';
import { IFirmApplication } from 'app/models/firms/firm-application';
import { IFirmCertificate } from 'app/models/firms/firm-certificate';
import { IFirmContactInformation } from 'app/models/firms/firm-contact-information';
import { IFirmCredential } from 'app/models/firms/firm-credential';
import { IFirmPerson } from 'app/models/firms/firm-person';
import { IFirmWorkspace } from 'app/models/firms/firm-workspace';
import { ConfirmationDialogueComponent } from 'app/components/common/confirmation-dialogue/confirmation-dialogue.component';
import { CommonService } from 'app/services/common/common.service';
import { SharedService } from 'app/services/core/shared.service';
import { FirmService } from 'app/services/firms/firm.service';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { StaffAffiliationRequestDialogComponent } from '../../common/staff-affiliation-request-dialog/staff-affiliation-request-dialog.component';
import { FirmApplicationComponent } from '../firm-application/firm-application.component';
import { FirmEditAddressDialogComponent } from './firm-edit-address-dialog.component';
import { FirmEditContactInformationDialogComponent } from './firm-edit-contact-information-dialog.component';
import { FirmEditCredentialDialogComponent } from './firm-edit-credential-dialog.component';
import {
  PUBLIC_FIRM_ADDRESS_TYPES,
  PUBLIC_FIRM_CONTACT_TYPES,
  PUBLIC_FIRM_CREDENTIAL_MAPPING,
} from './firm-edit.constants';

interface IAddressRow {
  original: any;
  id: number;
  contactType: string;
  street: string;
  city: string;
  state: string;
  zip: string;
  isCurrentPhysical: boolean;
  isCurrentMailing: boolean;
}

interface IContactInformationRow {
  original: any;
  id: number;
  contactType: string;
  phone: string;
  cell: string;
  fax: string;
  email: string;
  website: string;
}

interface IStaffRow {
  id: number;
  firmId: number;
  person: any;
  contactInformation: any;
  personId: number | null;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  cell: string;
  fax: string;
  isPOC: boolean;
  requestStatus: string;
  isRequestOnly: boolean;
}

@Component({
  selector: 'app-firm-edit',
  templateUrl: './firm-edit.component.html',
  styleUrls: ['./firm-edit.component.scss']
})
export class FirmEditComponent implements OnInit {
  public isMobile = false;
  public loading = true;
  public saving = false;
  public didPersistChange = false;
  public showHistory = false;
  public selectedTabIndex = 0;
  public uiData = LEAD_BASED_PAINT;
  public credentialMapping = PUBLIC_FIRM_CREDENTIAL_MAPPING;
  public workspace: IFirmWorkspace | null = null;
  public firm: IFirm | null = null;
  public firmId = 0;
  public firmForm!: UntypedFormGroup;
  public visibleApplicationCount = 0;

  public firmApplicationCount = 0;

  public firmCredentials: IFirmCredential[] = [];
  public firmValidatedCredentials: any[] = [];
  public firmFilteredCredentials: any[] = [];
  public credentialFilterString = '';
  private readonly credentialDebounceString: Subject<string> = new Subject<string>();
  public credentialDataSource!: MatTableDataSource<any>;
  public credentialTable = {
    layout: {
      columns: ['firmLeadCredentialType', 'isValid', 'totalStaff', 'staffIds', 'expirationDate'],
      container: [
        { displayName: 'Credential Type', columnName: 'firmLeadCredentialType', type: 'string', size: '28' },
        { displayName: 'Certified', columnName: 'isValid', type: 'bool', size: '12' },
        { displayName: 'Total Certified Staff', columnName: 'totalStaff', type: 'string', size: '14' },
        { displayName: 'Certified Staff', columnName: 'staffIds', type: 'string', size: '30' },
        { displayName: 'Expiration Date', columnName: 'expirationDate', type: 'date', size: '16' },
      ],
      data: [] as any[]
    }
  };
  public credentialTablePageSize = 5;
  @ViewChild('CredentialTablePaginator') credentialPaginator!: MatPaginator;
  @ViewChild('CredentialTableSort') credentialSort!: MatSort;

  public firmCertificates: IFirmCertificate[] = [];
  public firmFilteredCertificates: IFirmCertificate[] = [];
  public certificateFilterString = '';
  private readonly certificateDebounceString: Subject<string> = new Subject<string>();
  public certificateStageFilter = new FormControl<string>('Both', { nonNullable: true });
  public certificatesDataSource!: MatTableDataSource<any>;
  public certificateTable = {
    layout: {
      columns: ['codeType', 'stage', 'certificateNumber', 'controlNumber', 'issueDate', 'expirationDate', 'dateOfLetter'],
      container: [
        { displayName: 'Discipline', columnName: 'codeType', type: 'string', size: '16' },
        { displayName: 'Stage', columnName: 'stage', type: 'string', size: '10' },
        { displayName: 'Certificate Number', columnName: 'certificateNumber', type: 'string', size: '18' },
        { displayName: 'Control Number', columnName: 'controlNumber', type: 'string', size: '16' },
        { displayName: 'Issue Date', columnName: 'issueDate', type: 'date', size: '14' },
        { displayName: 'Expiration Date', columnName: 'expirationDate', type: 'date', size: '14' },
        { displayName: 'Date of Letter', columnName: 'dateOfLetter', type: 'date', size: '12' },
      ],
      data: [] as any[]
    }
  };
  public certificateTablePageSize = 5;
  @ViewChild('CertificateTablePaginator') certificatePaginator!: MatPaginator;
  @ViewChild('CertificateTableSort') certificateSort!: MatSort;

  public firmStaff: IFirmPerson[] = [];
  public firmStaffRows: IFirmPerson[] = [];
  public firmFilteredStaff: IStaffRow[] = [];
  public staffFilterString = '';
  private readonly staffDebounceString: Subject<string> = new Subject<string>();
  public staffDataSource!: MatTableDataSource<any>;
  public staffTable = {
    layout: {
      columns: ['firstName', 'lastName', 'email', 'requestStatus', 'phone', 'cell', 'fax', 'isPOC'],
      container: [
        { displayName: 'First Name', columnName: 'firstName', type: 'string', size: '11' },
        { displayName: 'Last Name', columnName: 'lastName', type: 'string', size: '11' },
        { displayName: 'Email', columnName: 'email', type: 'string', size: '20' },
        { displayName: 'Status', columnName: 'requestStatus', type: 'string', size: '10' },
        { displayName: 'Phone', columnName: 'phone', type: 'string', size: '10' },
        { displayName: 'Cell', columnName: 'cell', type: 'string', size: '10' },
        { displayName: 'Fax', columnName: 'fax', type: 'string', size: '8' },
        { displayName: 'Point of Contact', columnName: 'isPOC', type: 'bool', size: '12' },
      ],
      data: [] as IStaffRow[]
    }
  };
  public staffTablePageSize = 5;
  @ViewChild('StaffTablePaginator') staffPaginator!: MatPaginator;
  @ViewChild('StaffTableSort') staffSort!: MatSort;

  public addressTableColumns = ['contactType', 'street', 'city', 'state', 'zip', 'isCurrentPhysical', 'isCurrentMailing', 'actions'];
  public addressTableFormat = [
    { displayName: 'Type', columnName: 'contactType', type: 'string', size: '11' },
    { displayName: 'Street', columnName: 'street', type: 'string', size: '24' },
    { displayName: 'City', columnName: 'city', type: 'string', size: '15' },
    { displayName: 'State', columnName: 'state', type: 'string', size: '8' },
    { displayName: 'Zip', columnName: 'zip', type: 'string', size: '8' },
    { displayName: 'Current Physical', columnName: 'isCurrentPhysical', type: 'bool', size: '13' },
    { displayName: 'Current Mailing', columnName: 'isCurrentMailing', type: 'bool', size: '13' },
  ];
  public addressTableData: IAddressRow[] = [];
  public addressFilteredTableData: IAddressRow[] = [];
  public addressTablePageSize = 5;
  public filterAddressString = '';
  public filterAddressType = new FormControl<string[]>([], { nonNullable: true });
  public filterAddressPhysical = new FormControl<string[]>([], { nonNullable: true });
  public filterAddressMailing = new FormControl<string[]>([], { nonNullable: true });
  public addressTypeList = Object.values(PUBLIC_FIRM_ADDRESS_TYPES);
  public addressBoolList: string[] = ['Unchecked', 'Checked'];
  public addressDataSource!: MatTableDataSource<any>;
  @ViewChild('AddressTablePaginator') addressPaginator!: MatPaginator;
  @ViewChild('AddressTableSort') addressSort!: MatSort;

  public contactInformationTableColumns = ['contactType', 'phone', 'cell', 'fax', 'email', 'website', 'actions'];
  public contactInformationTableFormat = [
    { displayName: 'Type', columnName: 'contactType', type: 'string', size: '10' },
    { displayName: 'Phone', columnName: 'phone', type: 'string', size: '13' },
    { displayName: 'Cell', columnName: 'cell', type: 'string', size: '13' },
    { displayName: 'Fax', columnName: 'fax', type: 'string', size: '12' },
    { displayName: 'Email', columnName: 'email', type: 'string', size: '23' },
    { displayName: 'Website', columnName: 'website', type: 'string', size: '22' },
  ];
  public contactInformationTableData: IContactInformationRow[] = [];
  public contactInformationFilteredTableData: IContactInformationRow[] = [];
  public contactInformationTablePageSize = 5;
  public filterContactInformationString = '';
  public filterContactInformationType = new FormControl<string[]>([], { nonNullable: true });
  public contactInformationTypeList = Object.values(PUBLIC_FIRM_CONTACT_TYPES);
  public contactInformationDataSource!: MatTableDataSource<any>;
  @ViewChild('ContactInformationTablePaginator') contactInformationPaginator!: MatPaginator;
  @ViewChild('ContactInformationTableSort') contactInformationSort!: MatSort;

  public userAccounts: IAccountAffiliation[] = [];
  public userAccountsDataSource!: MatTableDataSource<any>;
  public userAccountTable = {
    layout: {
      columns: ['userName', 'userID', 'status', 'requestedStatus', 'lastUpdatedBy', 'actions'],
      container: [
        { displayName: 'User Name', columnName: 'userName', type: 'string', size: '23' },
        { displayName: 'User Email', columnName: 'userID', type: 'string', size: '25' },
        { displayName: 'Status', columnName: 'status', type: 'string', size: '15' },
        { displayName: 'Requested Status', columnName: 'requestedStatus', type: 'string', size: '15' },
        { displayName: 'Requested By', columnName: 'lastUpdatedBy', type: 'string', size: '15' }
      ],
      data: [] as IAccountAffiliation[]
    }
  };
  public userAccountTablePageSize = 5;
  public filterUserAccountString = '';
  @ViewChild('UserAccountTablePaginator') userAccountPaginator!: MatPaginator;
  @ViewChild('UserAccountTableSort') userAccountSort!: MatSort;

  constructor(
    @Optional() @Inject(MAT_DIALOG_DATA) public data: { firmId: number } | null,
    @Optional() private readonly dialogRef: MatDialogRef<FirmEditComponent> | null,
    private readonly breakpointObserver: BreakpointObserver,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly dialog: MatDialog,
    private readonly commonService: CommonService,
    private readonly firmService: FirmService,
    public readonly sharedService: SharedService,
    private readonly toastr: ToastrService,
  ) {
    this.breakpointObserver.observe([
      Breakpoints.Handset,
      Breakpoints.Tablet,
      Breakpoints.Small,
    ]).subscribe((result) => {
      this.isMobile = result.matches;
    });
  }

  ngOnInit(): void {
    this.initializeForms();
    this.initializeDebounceFunctions();

    if (this.data?.firmId) {
      this.firmId = this.data.firmId;
      this.loadWorkspace();
      return;
    }

    this.route.queryParamMap.subscribe((params) => {
      const rawFirmId = params.get('firmId');
      const nextFirmId = rawFirmId ? Number(rawFirmId) : NaN;

      if (!nextFirmId) {
        this.toastr.error('Firm not found.');
        this.router.navigate(['/firms/management']);
        return;
      }

      this.firmId = nextFirmId;
      this.loadWorkspace();
    });
  }

  statusColor(status: string): string {
    return this.sharedService.getStatusColor(status);
  }

  initializeForms(): void {
    this.firmForm = new UntypedFormGroup({
      isPrivate: new UntypedFormControl(false),
      inactive: new UntypedFormControl(false),
      name: new UntypedFormControl('', [Validators.required]),
      certificationNumber: new UntypedFormControl(''),
      servicesAvailable: new UntypedFormControl(''),
      propertyManagementLicense: new UntypedFormControl(''),
      ccb: new UntypedFormControl(''),
      ccbExpirationDate: new UntypedFormControl(''),
      coverage: new UntypedFormControl(''),
    });
    this.firmForm.get('certificationNumber')?.disable({ emitEvent: false });
    this.firmForm.get('inactive')?.disable({ emitEvent: false });
  }

  initializeDebounceFunctions(): void {
    this.certificateDebounceString.pipe(
      debounceTime(400),
      distinctUntilChanged()
    ).subscribe((value) => {
      this.certificateFilterString = value.trim().toLowerCase();
      this.filterCertificateTable();
    });

    this.staffDebounceString.pipe(
      debounceTime(400),
      distinctUntilChanged()
    ).subscribe((value) => {
      this.staffFilterString = value.trim().toLowerCase();
      this.filterStaffTable();
    });

    this.credentialDebounceString.pipe(
      debounceTime(400),
      distinctUntilChanged()
    ).subscribe((value) => {
      this.credentialFilterString = value.trim().toLowerCase();
      this.filterCredentialTable();
    });
  }

  loadWorkspace(): void {
    this.loading = true;
    this.firmService.getFirmWorkspace(this.firmId).subscribe({
      next: (workspace) => {
        this.workspace = workspace;
        this.firm = workspace.firm;
        this.visibleApplicationCount = workspace.applications.length;
        this.fillFirmForm();
        this.loadAddressData(workspace);
        this.loadContactInformationData(workspace);
        this.loadCertificateData(workspace);
        this.loadStaffData(workspace);
        this.loadCredentialData(workspace);
        this.loadUserAccounts(workspace.linkedUsers || []);
        this.loading = false;
      },
      error: (error) => {
        this.toastr.error('Error loading Firm workspace: ' + (error?.message || error?.error || 'Unknown error'));
        this.loading = false;
      }
    });
  }

  fillFirmForm(): void {
    if (!this.firm) {
      return;
    }

    const firmRecord = this.firm as unknown as Record<string, any>;

    for (const controlName in this.firm) {
      if (Object.prototype.hasOwnProperty.call(this.firmForm.controls, controlName)) {
        this.firmForm.controls[controlName].setValue(firmRecord[controlName]);
      }
    }

    this.firmForm.get('certificationNumber')?.disable({ emitEvent: false });
  }

  loadAddressData(workspace: IFirmWorkspace): void {
    this.addressTableData = (workspace.addresses || []).map((address) => ({
      original: { ...address },
      id: address.id,
      contactType: address.address?.contactType || '',
      street: address.address?.street || '',
      city: address.address?.city || '',
      state: address.address?.state || '',
      zip: address.address?.zip || '',
      isCurrentPhysical: address.isCurrentPhysical,
      isCurrentMailing: address.isCurrentMailing,
    })).sort((left, right) => {
      const leftIsCurrent = left.isCurrentPhysical || left.isCurrentMailing;
      const rightIsCurrent = right.isCurrentPhysical || right.isCurrentMailing;
      if (leftIsCurrent && !rightIsCurrent) {
        return -1;
      }
      if (!leftIsCurrent && rightIsCurrent) {
        return 1;
      }
      return 0;
    });
    this.filterAddressTable();
  }

  loadContactInformationData(workspace: IFirmWorkspace): void {
    this.contactInformationTableData = (workspace.contacts || []).map((contact) => ({
      original: contact,
      id: contact.id,
      contactType: contact.contactInformation?.contactType || '',
      phone: contact.contactInformation?.phone || '',
      cell: contact.contactInformation?.cell || '',
      fax: contact.contactInformation?.fax || '',
      email: contact.contactInformation?.email || '',
      website: contact.contactInformation?.website || '',
    }));
    this.filterContactInformationTable();
  }

  loadCertificateData(workspace: IFirmWorkspace): void {
    this.firmCertificates = [...(workspace.certificates || [])].sort(
      (left, right) => new Date(right.issueDate || 0).getTime() - new Date(left.issueDate || 0).getTime()
    );
    this.updateCalculatedInactiveState();
    this.filterCertificateTable();
  }

  private updateCalculatedInactiveState(): void {
    if (!this.firm) {
      return;
    }

    const hasValidCertificate = this.firmCertificates.some((certificate) => this.isValidCertificate(certificate));
    const inactive = !hasValidCertificate;

    this.firm.inactive = inactive;
    this.firmForm.get('inactive')?.setValue(inactive, { emitEvent: false });
    this.firmForm.get('inactive')?.disable({ emitEvent: false });
  }

  private isValidCertificate(certificate: IFirmCertificate): boolean {
    if (!certificate?.expirationDate) {
      return false;
    }

    const expirationDate = new Date(certificate.expirationDate);
    if (isNaN(expirationDate.getTime())) {
      return false;
    }

    const today = this.toDateOnly(new Date());
    return this.toDateOnly(expirationDate) >= today;
  }

  loadStaffData(workspace: IFirmWorkspace): void {
    this.firmStaffRows = [...(workspace.people || [])].sort((left, right) => {
      const leftName = (left.person?.firstName || '') + (left.person?.lastName || '');
      const rightName = (right.person?.firstName || '') + (right.person?.lastName || '');
      return leftName.localeCompare(rightName);
    });

    this.firmStaff = this.firmStaffRows.filter((staff) => staff?.isRequestOnly !== true && !!staff?.person?.id);

    this.staffTable.layout.data = this.firmStaffRows.map((staff) => ({
      ...staff,
      person: staff.person,
      personId: staff.person?.id || null,
      firstName: staff.person?.firstName || '',
      lastName: staff.person?.lastName || '',
      email: staff.contactInformation?.email || staff.person?.preferredEmail || '',
      requestStatus: this.normalizeStaffRequestStatus((staff as any).requestStatus),
      isRequestOnly: (staff as any).isRequestOnly === true,
      phone: staff.contactInformation?.phone || '',
      cell: staff.contactInformation?.cell || '',
      fax: staff.contactInformation?.fax || '',
      isPOC: staff.isPoc === true || (staff as any).isPOC === true,
    }));

    this.filterStaffTable();
  }

  loadCredentialData(workspace: IFirmWorkspace): void {
    this.firmCredentials = [...(workspace.credentials || [])].sort((left, right) =>
      (left.firmLeadCredentialType || '').localeCompare(right.firmLeadCredentialType || '')
    );
    this.firmValidatedCredentials = this.firmCredentials.map((credential) => ({ ...credential }));
    this.getStaffCerts();
  }

  loadUserAccounts(accounts: IAccountAffiliation[]): void {
    this.userAccounts = accounts || [];
    this.userAccountTable.layout.data = this.userAccounts;
    this.updateUserAccountDataSource();
  }

  addressCheckboxChange(row: IAddressRow, fieldName: string): void {
    const key = fieldName as 'isCurrentPhysical' | 'isCurrentMailing';
    row[key] = !row[key];
    row.original[key] = row[key];

    this.firmService.saveFirmAddress(row.original).subscribe({
      next: () => {
        this.didPersistChange = true;
        this.toastr.success('Current address updated');
        this.loadWorkspace();
      },
      error: (error) => {
        this.toastr.error('An error occurred saving the address: ' + (error?.message || error?.error || 'Unknown error'));
        this.loadWorkspace();
      }
    });
  }

  getStaffCerts(): void {
    if (this.firmStaff.length > 0 && this.firmCredentials.length > 0) {
      const credentials = this.firmStaff.map((entry) => ({
        person: entry.person,
        certs: entry.person?.certificates || [],
      }));
      this.validateFirmCredentials(credentials);
    }

    this.filterCredentialTable();
  }

  validateFirmCredentials(staffCerts: any): void {
    this.firmValidatedCredentials.forEach((credential) => {
      const value = this.firmCredentialsMappedToPersonCerts(credential, staffCerts);
      credential.isValid = value.isValid;
      credential.staffIds = value.staffIds;
      credential.totalStaff = value.staffIds.length;
      credential.expirationDate = value.expirationDate;
    });
  }

  firmCredentialsMappedToPersonCerts(credential: any, staff: any[]): any {
    const returnObject: { isValid: boolean; staffIds: any[]; expirationDate: string } = { isValid: false, staffIds: [], expirationDate: '' };
    const mappedTypes = this.credentialMapping[credential.firmLeadCredentialType] ?? [];
    const credentialTypes = Array.isArray(mappedTypes) ? mappedTypes : [mappedTypes];
    const now = new Date();
    const latestByPerson = new Map<any, any>();
    let furthestExpirationTime = 0;
    let furthestExpirationIso = '';

    staff.forEach((member) => {
      (member.certs || []).forEach((cert: any) => {
        if (!credentialTypes.includes(cert.codeType)) {
          return;
        }

        const expiration = cert.expirationDate ? new Date(cert.expirationDate) : null;
        if (!expiration || isNaN(expiration.getTime()) || expiration < now) {
          return;
        }

        returnObject.isValid = true;

        const personId = cert.person?.id;
        const staffObject = {
          id: personId,
          display: cert.person?.id + ': ' + cert.person?.firstName + ' ' + cert.person?.lastName,
          expiration: cert.expirationDate,
          codeType: cert.codeType
        };

        if (!latestByPerson.has(personId)) {
          latestByPerson.set(personId, staffObject);
        } else {
          const existing = latestByPerson.get(personId);
          const existingDate = existing?.expiration ? new Date(existing.expiration) : null;
          if (!existingDate || expiration > existingDate) {
            latestByPerson.set(personId, staffObject);
          }
        }

        if (expiration.getTime() > furthestExpirationTime) {
          furthestExpirationTime = expiration.getTime();
          furthestExpirationIso = expiration.toISOString();
        }
      });
    });

    returnObject.staffIds = Array.from(latestByPerson.values())
      .sort((left: any, right: any) => new Date(right.expiration).getTime() - new Date(left.expiration).getTime());
    returnObject.expirationDate = furthestExpirationIso;

    return returnObject;
  }

  displayLimit(text: any, limit: any): string {
    let nextText = text;
    if (typeof text === 'string' && text.length > Number(limit)) {
      nextText = nextText.substring(0, Number(limit)) + '...';
    }
    return nextText || '';
  }

  getCredentialCellTooltip(row: any, cellData: any): string {
    const value = row?.[cellData?.columnName];

    if (cellData?.columnName === 'staffIds') {
      if (!Array.isArray(value) || value.length === 0) {
        return '';
      }

      return value
        .map((staff: any) => staff?.display)
        .filter((display: string) => !!display)
        .join(', ');
    }

    if (value === null || value === undefined) {
      return '';
    }

    return String(value);
  }

  getFirmServiceForTypeValues(): string[] {
    return Object.values(FIRM_SERVICE_FOR);
  }

  get inDialog(): boolean {
    return !!this.dialogRef;
  }

  get exitButtonLabel(): string {
    return this.inDialog ? 'Exit' : 'Back to My Firms';
  }

  saveChanges(): void {
    if (!this.firm || !this.firmForm.valid) {
      this.toastr.error('Please fix validation errors before saving.');
      return;
    }

    this.saving = true;
    this.updateFirmData();

    this.firmService.saveFirm(this.firm).subscribe({
      next: () => {
        this.didPersistChange = true;
        this.toastr.success('Firm updated');
        this.loadWorkspace();
        this.saving = false;
      },
      error: (error) => {
        this.toastr.error('Error saving Firm: ' + (error?.message || error?.error || 'Unknown error'));
        this.saving = false;
      }
    });
  }

  updateFirmData(): void {
    if (!this.firm) {
      return;
    }

    const firmRecord = this.firm as unknown as Record<string, any>;

    Object.keys(this.firmForm.controls).forEach((controlName) => {
      if (controlName === 'certificationNumber') {
        return;
      }

      if (Object.prototype.hasOwnProperty.call(this.firm, controlName)) {
        firmRecord[controlName] = this.firmForm.controls[controlName].value;
      }
    });
  }

  exitComponent(): void {
    if (this.dialogRef) {
      this.dialogRef.close(this.didPersistChange);
      return;
    }

    this.router.navigate(['/firms/management']);
  }

  editAddressTableEntry(row: IAddressRow | null): void {
    if (!this.firm) {
      return;
    }

    const firmAddress: IFirmAddress = row?.original ?? {
      id: 0,
      firmId: this.firm.id,
      address: { id: 0, street: '', city: '', state: '', zip: '', county: '', contactType: '' },
      isCurrentPhysical: false,
      isCurrentMailing: false,
    };

    const dialogRef = this.dialog.open(FirmEditAddressDialogComponent, {
      width: this.isMobile ? '70%' : '30%',
      autoFocus: false,
      data: { address: { ...firmAddress, address: { ...firmAddress.address } } },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.didPersistChange = true;
        this.loadWorkspace();
      }
    });
  }

  deleteAddressEntry(row: IAddressRow): void {
    const dialogRef = this.dialog.open(ConfirmationDialogueComponent, {
      width: '400px',
      data: {
        message: 'Are you sure you want to <strong>Delete</strong> the Address at: <strong>' + row.street + '</strong>?',
        title: 'Delete the Address'
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.firmService.deleteFirmAddress(row.original.id).subscribe({
          next: () => {
            this.didPersistChange = true;
            this.toastr.success('Address deleted successfully');
            this.loadWorkspace();
          },
          error: (error) => {
            this.toastr.error('Error deleting Address: ' + (error?.message || error?.error || 'Unknown error'));
          }
        });
      }
    });
  }

  editContactInformationTableEntry(row: IContactInformationRow | null): void {
    if (!this.firm) {
      return;
    }

    const firmContactInformation: IFirmContactInformation = row?.original ?? {
      id: 0,
      firmId: this.firm.id,
      contactInformation: { id: 0, phone: '', ext: '', cell: '', fax: '', email: '', website: '', contactType: '' },
    };

    const dialogRef = this.dialog.open(FirmEditContactInformationDialogComponent, {
      width: this.isMobile ? '70%' : '30%',
      autoFocus: false,
      data: {
        contactInformation: {
          ...firmContactInformation,
          contactInformation: { ...firmContactInformation.contactInformation },
        }
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.didPersistChange = true;
        this.loadWorkspace();
      }
    });
  }

  deleteContactInformationEntry(row: IContactInformationRow): void {
    const dialogRef = this.dialog.open(ConfirmationDialogueComponent, {
      width: '400px',
      data: {
        message: 'Are you sure you want to <strong>Delete</strong> the Contact Information with ID: <strong>' + row.id + '</strong>?',
        title: 'Delete the Contact Information'
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.firmService.deleteFirmContactInformation(row.original.id).subscribe({
          next: () => {
            this.didPersistChange = true;
            this.toastr.success('Contact Information deleted successfully');
            this.loadWorkspace();
          },
          error: (error) => {
            this.toastr.error('Error deleting Contact Information: ' + (error?.message || error?.error || 'Unknown error'));
          }
        });
      }
    });
  }

  openRenewalApplication(previousApplication?: string | null): void {
    if (!this.firm) {
      return;
    }

    const dialogRef = this.dialog.open(FirmApplicationComponent, {
      width: '95%',
      height: this.isMobile ? '95%' : '94vh',
      autoFocus: false,
      data: {
        workflow: 'renewal',
        firmId: this.firm.id,
        previousApplication: previousApplication || null,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.didPersistChange = true;
        this.loadWorkspace();
      }
    });
  }

  openCertificateAction(row: IFirmCertificate): void {
    const pendingApplication = this.getPendingApplicationForCertificate(row);
    if (pendingApplication) {
      this.openApplication(pendingApplication.id);
      return;
    }

    this.openRenewalApplication(row.controlNumber || null);
  }

  getCertificateActionLabel(row: IFirmCertificate): string {
    return this.getPendingApplicationForCertificate(row) ? 'View' : 'Renew';
  }

  private getPendingApplicationForCertificate(certificate: IFirmCertificate | null | undefined): IFirmApplication | undefined {
    if (!certificate || !this.workspace) {
      return undefined;
    }

    const targetType = this.getApplicationTypeForCertificate(certificate);
    if (!targetType) {
      return undefined;
    }

    const matchingApplications = this.workspace.applications
      .filter((application) => this.normalizeStatus(application.applicationType) === this.normalizeStatus(targetType))
      .filter((application) => this.normalizeStatus(application.applicationStatus) !== this.normalizeStatus(APPLICATION_STATUS.approved))
      .filter((application) => this.normalizeStatus(application.applicationStatus) !== this.normalizeStatus(APPLICATION_STATUS.cancelled))
      .filter((application) => !this.isApplicationExpired(application))
      .sort((left, right) => {
        const leftDate = new Date(left.applicationDate || 0).getTime();
        const rightDate = new Date(right.applicationDate || 0).getTime();
        return rightDate - leftDate;
      });

    return matchingApplications[0];
  }

  private getApplicationTypeForCertificate(certificate: IFirmCertificate): string | null {
    const codeType = this.normalizeStatus(certificate.codeType);
    if (!codeType) {
      return null;
    }

    return codeType.includes('rrp')
      ? FIRM_APPLICATION_TYPES.rrp
      : FIRM_APPLICATION_TYPES.lbpa;
  }

  private isApplicationExpired(application: IFirmApplication | null | undefined): boolean {
    if (!application?.applicationDate) {
      return false;
    }

    const status = this.normalizeStatus(application.applicationStatus);
    if (status === this.normalizeStatus(APPLICATION_STATUS.approved)
      || status === this.normalizeStatus(APPLICATION_STATUS.cancelled)) {
      return false;
    }

    const currentDate = this.toDateOnly(new Date());
    const applicationDate = this.toDateOnly(new Date(application.applicationDate));
    const diffInDays = (currentDate.getTime() - applicationDate.getTime()) / (1000 * 3600 * 24);
    return diffInDays > 90;
  }

  private normalizeStatus(status: string | null | undefined): string {
    return (status || '').toString().trim().toLowerCase();
  }

  private toDateOnly(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }

  private openApplication(applicationId: number): void {
    const dialogRef = this.dialog.open(FirmApplicationComponent, {
      width: '95%',
      autoFocus: false,
      data: { applicationId },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.didPersistChange = true;
        this.loadWorkspace();
      }
    });
  }

  onApplicationCountChanged(count: number): void {
    this.visibleApplicationCount = count;
  }

  onTabIndexChange(index: number): void {
    this.selectedTabIndex = index;
  }

  updateCertificateDebounceString(event: any): void {
    this.certificateDebounceString.next(event.target.value);
  }

  filterCertificateTable(): void {
    if (this.showHistory) {
      this.firmFilteredCertificates = this.firmCertificates.filter((certificate) =>
        (certificate.id + ' ' + certificate.codeType + ' ' + certificate.controlNumber).toLowerCase().includes(this.certificateFilterString)
        && ((this.certificateStageFilter.value === 'Both') ? true : (certificate.stage === this.certificateStageFilter.value))
      );
    } else {
      this.firmFilteredCertificates = this.firmCertificates
        .filter((certificate) =>
          (certificate.id + ' ' + certificate.codeType + ' ' + certificate.controlNumber).toLowerCase().includes(this.certificateFilterString)
          && ((this.certificateStageFilter.value === 'Both') ? true : (certificate.stage === this.certificateStageFilter.value))
        )
        .reduce((accumulator, current) => {
          const existing = accumulator.find((certificate) => certificate.codeType === current.codeType);
          if (!existing) {
            accumulator.push(current);
          } else if (new Date(current.expirationDate || 0) > new Date(existing.expirationDate || 0)) {
            const index = accumulator.indexOf(existing);
            accumulator[index] = current;
          }
          return accumulator;
        }, [] as IFirmCertificate[]);
    }

    this.updateCertificateSource();
  }

  updateCertificateSource(): void {
    this.certificatesDataSource = new MatTableDataSource<any>(this.firmFilteredCertificates);
    this.certificatesDataSource.sort = this.certificateSort;
    this.certificatesDataSource.paginator = this.certificatePaginator;
  }

  updateCredentialDebounceString(event: any): void {
    this.credentialDebounceString.next(event.target.value);
  }

  filterCredentialTable(): void {
    this.firmFilteredCredentials = this.firmValidatedCredentials.filter((credential) =>
      credential.firmLeadCredentialType.toLowerCase().includes(this.credentialFilterString)
    );
    this.updateCredentialSource();
  }

  updateCredentialSource(): void {
    this.credentialDataSource = new MatTableDataSource<any>(this.firmFilteredCredentials);
    this.credentialDataSource.sort = this.credentialSort;
    this.credentialDataSource.paginator = this.credentialPaginator;
  }

  editCredential(row: any): void {
    if (!this.firm) {
      return;
    }

    const dialogRef = this.dialog.open(FirmEditCredentialDialogComponent, {
      width: this.isMobile ? '90%' : '35%',
      autoFocus: false,
      data: {
        firm: this.firm,
        credential: row ? { id: row.id, firmLeadCredentialType: row.firmLeadCredentialType } : null,
        firmCredentials: this.firmCredentials,
        staffCerts: this.firmStaff.map((entry) => ({ person: entry.person, certs: entry.person?.certificates || [] })),
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.didPersistChange = true;
        this.loadWorkspace();
      }
    });
  }

  deleteCredential(row: any): void {
    const dialogRef = this.dialog.open(ConfirmationDialogueComponent, {
      width: '400px',
      data: {
        message: 'Are you sure you want to remove the firm credential: <strong>' + row.firmLeadCredentialType + '</strong>?',
        title: 'Delete Firm Credential'
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.firmService.deleteFirmCredential(row.id).subscribe({
          next: () => {
            this.didPersistChange = true;
            this.toastr.success('Successfully removed firm credential');
            this.loadWorkspace();
          },
          error: (error) => {
            this.toastr.error('An error occurred trying to remove the firm credential: ' + (error?.message || error?.error || 'Unknown error'));
          }
        });
      }
    });
  }

  updateStaffDebounceString(event: any): void {
    this.staffDebounceString.next(event.target.value);
  }

  filterStaffTable(): void {
    this.firmFilteredStaff = this.staffTable.layout.data.filter((staff) =>
      (
        (staff.firstName || '')
        + ' '
        + (staff.lastName || '')
        + ' '
        + (staff.email || '')
        + ' '
        + (staff.requestStatus || '')
      ).toLowerCase().includes(this.staffFilterString)
    );
    this.updateStaffSource();
  }

  updateStaffSource(): void {
    this.staffDataSource = new MatTableDataSource<any>(this.firmFilteredStaff);
    this.staffDataSource.sort = this.staffSort;
    this.staffDataSource.paginator = this.staffPaginator;
  }

  openAddStaffRequestDialog(): void {
    if (!this.firm) {
      return;
    }

    const existingPersonIds = this.staffTable.layout.data
      .map((staff) => staff.personId)
      .filter((personId) => !!personId);

    const dialogRef = this.dialog.open(StaffAffiliationRequestDialogComponent, {
      width: this.isMobile ? '95%' : '45%',
      autoFocus: false,
      data: {
        title: 'Add Firm Staff',
        existingPersonIds,
      },
    });

    dialogRef.afterClosed().subscribe((selectedPerson) => {
      if (!selectedPerson?.id || !this.firm) {
        return;
      }

      this.firmService.requestFirmStaffAffiliation(this.firm.id, selectedPerson.id).subscribe({
        next: (response) => {
          if (response?.success === false) {
            this.toastr.info(response?.message || 'This person is already affiliated to this Firm.');
            this.loadWorkspace();
            return;
          }

          this.didPersistChange = true;
          this.toastr.success('Staff affiliation approval request sent.');
          this.loadWorkspace();
        },
        error: (error) => {
          this.toastr.error('Error requesting Firm staff affiliation: ' + (error?.message || error?.error || 'Unknown error'));
        }
      });
    });
  }

  cancelStaffAffiliationRequest(row: IStaffRow): void {
    if (!this.firm) {
      return;
    }

    const personId = row?.personId;
    if (!personId) {
      return;
    }

    this.firmService.cancelFirmStaffAffiliationRequest(this.firm.id, personId).subscribe({
      next: (response) => {
        if (response?.success) {
          this.didPersistChange = true;
          this.toastr.success('Staff affiliation request removed.');
        } else {
          this.toastr.info('No active staff affiliation request was found.');
        }

        this.loadWorkspace();
      },
      error: (error) => {
        this.toastr.error('Error removing Firm staff affiliation request: ' + (error?.message || error?.error || 'Unknown error'));
      }
    });
  }

  deleteStaffAffiliation(row: IStaffRow): void {
    if (!row || !this.firm) {
      return;
    }

    if (row.isRequestOnly === true) {
      this.cancelStaffAffiliationRequest(row);
      return;
    }

    if (!row.id) {
      this.toastr.error('Unable to remove staff affiliation.');
      return;
    }

    const fullName = ((row.firstName || '') + ' ' + (row.lastName || '')).trim() || 'this staff member';
    const dialogRef = this.dialog.open(ConfirmationDialogueComponent, {
      width: '400px',
      data: {
        message: 'Are you sure you want to <strong>Remove</strong> staff affiliation for <strong>' + fullName + '</strong>?',
        title: 'Remove Firm Staff Affiliation',
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result) {
        return;
      }

      this.firmService.deleteFirmPerson(row.id).subscribe({
        next: () => {
          this.didPersistChange = true;
          this.toastr.success('Staff affiliation removed successfully.');
          this.loadWorkspace();
        },
        error: (error) => {
          this.toastr.error('Error removing Firm staff affiliation: ' + (error?.message || error?.error || 'Unknown error'));
        }
      });
    });
  }

  canTogglePocForStaffRow(row: IStaffRow): boolean {
    return row?.isRequestOnly !== true;
  }

  getStaffRowClass(row: IStaffRow): string {
    const status = this.normalizeStaffRequestStatus(row?.requestStatus);
    if (status === 'Pending') {
      return 'affiliation-status-pending';
    }

    if (status === 'Denied') {
      return 'affiliation-status-denied';
    }

    return '';
  }

  getStaffStatusClass(row: IStaffRow): string {
    const status = this.normalizeStaffRequestStatus(row?.requestStatus);
    if (status === 'Pending') {
      return 'affiliation-status-pending';
    }

    if (status === 'Denied') {
      return 'affiliation-status-denied';
    }

    return '';
  }

  private normalizeStaffRequestStatus(value: string | null | undefined): string {
    const normalizedValue = (value || '').toString().trim().toLowerCase();
    if (normalizedValue === 'pending') {
      return 'Pending';
    }

    if (normalizedValue === 'denied') {
      return 'Denied';
    }

    return '';
  }

  updateStaffMemberPOCStatus(row: any): void {
    if (row?.isRequestOnly === true) {
      return;
    }

    const dialogRef = this.dialog.open(ConfirmationDialogueComponent, {
      width: '400px',
      data: {
        message: row.isPOC
          ? 'Are you sure you want to <strong>Remove</strong> the Contact Person: <strong>' + row.firstName + ' ' + row.lastName + '</strong>?'
          : 'Are you sure you want to <strong>Add</strong> the Contact Person: <strong>' + row.firstName + ' ' + row.lastName + '</strong>?',
        title: row.isPOC ? 'Remove the Contact Person' : 'Add the Contact Person'
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result || !this.firm) {
        return;
      }

      const updatedFirmPerson: IFirmPerson = {
        id: row.id,
        firmId: this.firm.id,
        person: row.person,
        contactInformation: row.contactInformation ?? null,
        isPoc: !row.isPOC,
      };

      this.firmService.saveFirmPerson(updatedFirmPerson).subscribe({
        next: () => {
          this.didPersistChange = true;
          this.toastr.success('Point of Contact status updated successfully');
          this.loadWorkspace();
        },
        error: (error) => {
          this.toastr.error('Error updating the Firm Staff: ' + (error?.message || error?.error || 'Unknown error'));
        }
      });
    });
  }

  filterAddressTable(): void {
    this.addressFilteredTableData = this.addressTableData;

    this.addressFilteredTableData = this.addressFilteredTableData.filter((address) => {
      return address.id?.toString().includes(this.filterAddressString)
        || address.street?.toLowerCase().includes(this.filterAddressString.toLowerCase())
        || address.city?.toLowerCase().includes(this.filterAddressString.toLowerCase())
        || address.zip?.toLowerCase().includes(this.filterAddressString.toLowerCase());
    });

    if (this.filterAddressType.value.length > 0) {
      this.addressFilteredTableData = this.addressFilteredTableData.filter((address) =>
        this.filterAddressType.value.includes(address.contactType)
      );
    }

    if (this.filterAddressPhysical.value.length > 0) {
      this.addressFilteredTableData = this.addressFilteredTableData.filter((address) =>
        (this.filterAddressPhysical.value.includes('Unchecked') && address.isCurrentPhysical === false)
        || (this.filterAddressPhysical.value.includes('Checked') && address.isCurrentPhysical === true)
      );
    }

    if (this.filterAddressMailing.value.length > 0) {
      this.addressFilteredTableData = this.addressFilteredTableData.filter((address) =>
        (this.filterAddressMailing.value.includes('Unchecked') && address.isCurrentMailing === false)
        || (this.filterAddressMailing.value.includes('Checked') && address.isCurrentMailing === true)
      );
    }

    this.updateAddressDataSource();
  }

  updateAddressDataSource(): void {
    this.addressDataSource = new MatTableDataSource<any>(this.addressFilteredTableData);
    this.addressDataSource.paginator = this.addressPaginator;
    this.addressDataSource.sort = this.addressSort;
  }

  filterContactInformationTable(): void {
    this.contactInformationFilteredTableData = this.contactInformationTableData;

    this.contactInformationFilteredTableData = this.contactInformationFilteredTableData.filter((contact) => {
      return contact.id?.toString().includes(this.filterContactInformationString)
        || contact.phone?.toLowerCase().includes(this.filterContactInformationString.toLowerCase())
        || contact.cell?.toLowerCase().includes(this.filterContactInformationString.toLowerCase())
        || contact.fax?.toLowerCase().includes(this.filterContactInformationString.toLowerCase())
        || contact.email?.toLowerCase().includes(this.filterContactInformationString.toLowerCase())
        || contact.website?.toLowerCase().includes(this.filterContactInformationString.toLowerCase());
    });

    if (this.filterContactInformationType.value.length > 0) {
      this.contactInformationFilteredTableData = this.contactInformationFilteredTableData.filter((contact) =>
        this.filterContactInformationType.value.includes(contact.contactType)
      );
    }

    this.updateContactInformationDataSource();
  }

  updateContactInformationDataSource(): void {
    this.contactInformationDataSource = new MatTableDataSource<any>(this.contactInformationFilteredTableData);
    this.contactInformationDataSource.paginator = this.contactInformationPaginator;
    this.contactInformationDataSource.sort = this.contactInformationSort;
  }

  applyUserAccountFilter(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value;
    this.filterUserAccountString = filterValue;

    if (!this.userAccountsDataSource) {
      return;
    }

    this.userAccountsDataSource.filter = filterValue.trim().toLowerCase();

    if (this.userAccountsDataSource.paginator) {
      this.userAccountsDataSource.paginator.firstPage();
    }
  }

  updateUserAccountDataSource(): void {
    const sortedAccounts = [...this.userAccounts].sort((left, right) => {
      const leftHasPendingRequest = left.status !== left.requestedStatus;
      const rightHasPendingRequest = right.status !== right.requestedStatus;
      const leftIsInactive = left.status === 'Inactive' && left.requestedStatus === 'Inactive';
      const rightIsInactive = right.status === 'Inactive' && right.requestedStatus === 'Inactive';

      if (leftHasPendingRequest && !rightHasPendingRequest) {
        return -1;
      }

      if (!leftHasPendingRequest && rightHasPendingRequest) {
        return 1;
      }

      if (!leftHasPendingRequest && !rightHasPendingRequest) {
        if (leftIsInactive && !rightIsInactive) {
          return 1;
        }

        if (!leftIsInactive && rightIsInactive) {
          return -1;
        }
      }

      return 0;
    });

    this.userAccountsDataSource = new MatTableDataSource<any>(sortedAccounts);
    this.userAccountsDataSource.paginator = this.userAccountPaginator;
    this.userAccountsDataSource.sort = this.userAccountSort;
    this.userAccountsDataSource.filter = this.filterUserAccountString.trim().toLowerCase();
  }

  getRowClass(row: IAccountAffiliation): string {
    if (row.status !== row.requestedStatus) {
      return 'pending-request-row';
    }

    if (row.status === 'Inactive') {
      return 'inactive-row';
    }

    return '';
  }

  shouldShowRequestedBy(row: IAccountAffiliation): boolean {
    return row.status !== row.requestedStatus;
  }

  toggleUserAccountRequestedStatus(row: IAccountAffiliation): void {
    const newRequestedStatus = row.requestedStatus === 'Active' ? 'Inactive' : 'Active';
    const action = newRequestedStatus === 'Active' ? 'Activate' : 'Deactivate';

    const affiliation: IAccountAffiliation = {
      ...row,
      userId: row.userId,
      requestedStatus: newRequestedStatus,
      userName: row.userName,
      lastUpdatedBy: row.lastUpdatedBy,
    };

    this.commonService.saveAccountAffiliation(affiliation).subscribe({
      next: () => {
        this.didPersistChange = true;
        this.toastr.success(action + ' request submitted successfully');
        this.loadWorkspace();
      },
      error: (error) => {
        this.toastr.error('Error submitting ' + action.toLowerCase() + ' request: ' + (error?.message || error?.error || 'Unknown error'));
      }
    });
  }
}
